import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden from "@insite/mobius/Hidden";
import { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS, TYPOGRAPHY } from "@tKgpco/theme/design";
import parse from "html-react-parser";
import React, { FC } from "react";
import { css } from "styled-components";

const enum fields {
    milestone = "milestone",
    title = "title",
    content = "content",
    line = "line",
    founded = "founded",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.milestone]: string;
        [fields.title]: string;
        [fields.content]: string;
        [fields.line]: string;
        [fields.founded]: string;
    };
}

export interface MilestoneStyles {
    milestone?: InjectableCss;
    title?: TypographyProps;
    content?: InjectableCss;
    smContent?: InjectableCss;
    line?: TypographyProps;
    founded?: TypographyProps;
    pathOne?: InjectableCss;
    waveOne?: InjectableCss;
    waveTwo?: InjectableCss;
    waveThree?: InjectableCss;
    waveFour?: InjectableCss;
    waveFive?: InjectableCss;
    waveSix?: InjectableCss;
    waveSeven?: InjectableCss;
    waveEight?: InjectableCss;
    waveNine?: InjectableCss;
    waveTen?: InjectableCss;
    waveEleven?: InjectableCss;
    waveTwelve?: InjectableCss;
    waveThirteen?: InjectableCss;
    logo?: InjectableCss;
    topRow?: GridContainerProps;
    bottomRow?: GridContainerProps;
    innerContainer?: GridItemProps;
    mincircleOne?: TypographyProps;
    mincircleTwo?: TypographyProps;
    boldCircleOne?: TypographyProps;
    boldCircleTwo?: TypographyProps;
    boldCircleThree?: TypographyProps;
    boldCircleFour?: TypographyProps;
    boldCircleFive?: TypographyProps;
    boldCircleSix?: TypographyProps;
    boldCircleSeven?: TypographyProps;
    boldCircleEight?: TypographyProps;
    boldCircleNine?: TypographyProps;
    boldCircleTen?: TypographyProps;
    boldCircleEleven?: TypographyProps;
    gridmain?: GridContainerProps;
    gridTop?: GridItemProps;
    gridMiddle?: GridItemProps;
    gridBottom?: GridItemProps;
    gridMiddleWrap?: InjectableCss;
    gridSm?: InjectableCss;
    smboldCircleOne?: InjectableCss;
    smboldCircleTwo?: InjectableCss;
    smMinCircleOne?: InjectableCss;
    smMinCircleTwo?: InjectableCss;
    extraInnerContainer?: GridItemProps;
    extraSmboldCircle?: InjectableCss;
}

export const milestoneStyles: MilestoneStyles = {
    milestone: {
        css: css`
            background: #122e4d;
            width: 100%;
            position: relative;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            overflow-y: auto;
                            padding: 0 !important;
                        `,
                        css`
                            padding: 45px 45px 0;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    title: {
        css: css`
            width: 100%;
            .title {
                font-size: 22px;
                color: ${COLORS.white};
                font-weight: 100;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            margin-bottom: 41px;
                            .title {
                                text-align: center;
                            }
                        `,
                    ],
                    "max",
                )}
        `,
    },
    content: {
        css: css`
            width: 100%;
            display: flex;
            align-items: center;
            height: 125px;
            position: relative;
            & > div :not(:first-child) {
                position: absolute;
                // width: 88px;
                width: 90px;
                height: 4.15rem;
            }
        `,
    },
    smContent: {
        css: css`
            height: 100%;
            display: flex;
            justify-content: center;
            position: relative;
        `,
    },
    line: {
        css: css`
            background: #1d7af8;
            height: 2px;
            width: 92%;
            position: relative;
            & > div:not(:first-child) {
                background: ${COLORS.background.base};
                width: 15px;
                height: 15px;
                border-radius: 50%;
                position: absolute;
                z-index: 1;
                top: -6px;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            background: #1d7af8;
                            height: 89%;
                            width: 2px;
                        `,
                    ],
                    "max",
                )}
        `,
    },

    founded: {
        css: css`
            padding: 1px 8px;
            border-radius: 23px;
            background: ${COLORS.background.base};
            font-size: ${TYPOGRAPHY.size.base};
            font-weight: ${TYPOGRAPHY.weight.black};
            position: absolute;
            top: -14px;
            color: ${COLORS.primary.light};
            z-index: 2;
            @media only screen and (min-device-width: 481px) and (max-device-width: 1024px) and (orientation: landscape) {
                padding: 1px 12px;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            padding: 3px 26px;
                            font-size: 18px;
                            width: 124px;
                            height: 32px;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    pathOne: {
        css: css``,
    },
    waveOne: {
        css: css`
            left: 1%;
            top: -2%;
            border-radius: 0 0 70% 0;
            border-right: 2px solid #00a5fb;
            border-bottom: 2px solid #00a5fb;
            border-left: 2px solid transparent;
            border-top: 2px solid transparent;
            & > div:first-child {
                border-left: 2px solid #00a5fb;
                border-top: 2px solid #00a5fb;
                border-right: 2px solid #00b3b1;
                border-bottom: 2px solid #00b3b1;
            }
            @media only screen and (max-width: 1199px) and (min-width: 992px) and (orientation: portrait) {
                width: 65px !important;
            }
            @media only screen and (min-device-width: 481px) and (max-device-width: 1024px) and (orientation: landscape) {
                width: 65px !important;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            left: 11% !important;
                            width: 43px !important;
                        `,
                        css`
                            left: 10.6%;
                            top: 0%;
                            border-radius: 0 0 77% 0;
                            border-right: 2px solid #00a5fb;
                            border-bottom: 2px solid #00a5fb;
                            position: absolute;
                            width: 82px;
                            height: 102px;
                            border-left: 2px solid transparent;
                            border-top: 2px solid transparent;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    waveTwo: {
        css: css`
            bottom: -2%;
            left: 8.5%;
            border-radius: 0 70% 0 0;
            border-right: 2px solid #00e8fb;
            border-top: 2px solid #00e8fb;
            border-left: 2px solid transparent;
            border-bottom: 2px solid transparent;
            & > div:first-child {
                border-left: 2px solid #00e8fb;
                border-top: 2px solid #00e8fb;
                border-right: 2px solid #00b330;
                border-bottom: 2px solid #00b330;
            }
            @media only screen and (max-width: 1199px) and (min-width: 992px) and (orientation: portrait) {
                width: 65px !important;
            }
            @media only screen and (min-device-width: 481px) and (max-device-width: 1024px) and (orientation: landscape) {
                width: 65px !important;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            width: 43px !important;
                        `,
                        css`
                            top: 6.5%;
                            left: 49.5%;
                            border-radius: 0 0 0 70%;
                            border-left: 2px solid #00e8fb;
                            border-bottom: 2px solid #00e8fb;
                            position: absolute;
                            width: 82px;
                            height: 116px;
                            border-right: 2px solid transparent;
                            border-top: 2px solid transparent;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    waveThree: {
        css: css`
            left: 16%;
            top: -2%;
            border-radius: 0 0 70% 0;
            border-right: 2px solid #a5b300;
            border-bottom: 2px solid #a5b300;
            border-left: 2px solid transparent;
            border-top: 2px solid transparent;
            & > div:first-child {
                border-left: 2px solid #a5b300;
                border-top: 2px solid #a5b300;
                border-right: 2px solid #00fb74;
                border-bottom: 2px solid #00fb74;
            }
            @media only screen and (max-width: 1199px) and (min-width: 992px) and (orientation: portrait) {
                width: 65px !important;
            }
            @media only screen and (min-device-width: 481px) and (max-device-width: 1024px) and (orientation: landscape) {
                width: 65px !important;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            left: 11.8% !important;
                            width: 43px !important;
                        `,
                        css`
                            left: 10.4%;
                            top: 13.9%;
                            border-radius: 0 0 70% 0;
                            border-right: 2px solid #a5b300;
                            border-bottom: 2px solid #a5b300;
                            position: absolute;
                            width: 82px;
                            height: 116px;
                            border-left: 2px solid transparent;
                            border-top: 2px solid transparent;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    waveFour: {
        css: css`
            bottom: -2%;
            left: 24%;
            border-radius: 0 70% 0 0;
            border-right: 2px solid #aefb00;
            border-top: 2px solid #aefb00;
            border-left: 2px solid transparent;
            border-bottom: 2px solid transparent;
            & > div:first-child {
                border-left: 2px solid #b39100;
                border-top: 2px solid #b39100;
                border-right: 2px solid #aefb00;
                border-bottom: 2px solid #aefb00;
            }
            @media only screen and (max-width: 1199px) and (min-width: 992px) and (orientation: portrait) {
                width: 65px !important;
            }
            @media only screen and (min-device-width: 481px) and (max-device-width: 1024px) and (orientation: landscape) {
                width: 65px !important;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            width: 43px !important;
                        `,
                        css`
                            top: 21%;
                            left: 49.5%;
                            border-radius: 0 0 0 70%;
                            border-left: 2px solid #aefb00;
                            border-bottom: 2px solid #aefb00;
                            position: absolute;
                            width: 82px;
                            height: 116px;
                            border-right: 2px solid transparent;
                            border-top: 2px solid transparent;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    waveFive: {
        css: css`
            left: 32%;
            top: -2%;
            border-radius: 0 0 70% 0;
            border-right: 2px solid #fbe800;
            border-bottom: 2px solid #fbe800;
            border-left: 2px solid transparent;
            border-top: 2px solid transparent;
            & > div:first-child {
                border-left: 2px solid #b37c00;
                border-top: 2px solid #b37c00;
                border-right: 2px solid #fbe800;
                border-bottom: 2px solid #fbe800;
            }
            @media only screen and (max-width: 1199px) and (min-width: 992px) and (orientation: portrait) {
                width: 65px !important;
            }
            @media only screen and (min-device-width: 481px) and (max-device-width: 1024px) and (orientation: landscape) {
                width: 65px !important;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            left: 11.8% !important;
                            width: 43px !important;
                        `,
                        css`
                            left: 10.4%;
                            top: 28.1%;
                            border-radius: 0 0 70% 0;
                            border-right: 2px solid #fbe800;
                            border-bottom: 2px solid #fbe800;
                            position: absolute;
                            width: 82px;
                            height: 116px;
                            border-left: 2px solid transparent;
                            border-top: 2px solid transparent;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    waveSix: {
        css: css`
            bottom: -2%;
            left: 39%;
            border-radius: 0 70% 0 0;
            border-right: 2px solid #d37100;
            border-top: 2px solid #d37100;
            border-left: 2px solid transparent;
            border-bottom: 2px solid transparent;
            & > div:first-child {
                border-left: 2px solid #d37100;
                border-top: 2px solid #d37100;
                border-right: 2px solid #fbb700;
                border-bottom: 2px solid #fbb700;
            }
            @media only screen and (max-width: 1199px) and (min-width: 992px) and (orientation: portrait) {
                width: 65px !important;
            }
            @media only screen and (min-device-width: 481px) and (max-device-width: 1024px) and (orientation: landscape) {
                width: 65px !important;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            width: 43px !important;
                        `,
                        css`
                            top: 35.2%;
                            left: 49.5%;
                            border-radius: 0 0 0 70%;
                            border-left: 2px solid #d37100;
                            border-bottom: 2px solid #d37100;
                            position: absolute;
                            width: 82px;
                            height: 116px;
                            border-right: 2px solid transparent;
                            border-top: 2px solid transparent;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    waveSeven: {
        css: css`
            left: 47%;
            top: -2%;
            border-radius: 0 0 70% 0;
            border-right: 2px solid #f28a11;
            border-bottom: 2px solid #f28a11;
            border-left: 2px solid transparent;
            border-top: 2px solid transparent;
            & > div:first-child {
                border-left: 2px solid #b33e00;
                border-top: 2px solid #b33e00;
                border-right: 2px solid #f28a11;
                border-bottom: 2px solid #f28a11;
            }
            @media only screen and (max-width: 1199px) and (min-width: 992px) and (orientation: portrait) {
                width: 65px !important;
            }
            @media only screen and (min-device-width: 481px) and (max-device-width: 1024px) and (orientation: landscape) {
                width: 65px !important;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            left: 11.8% !important;
                            width: 43px !important;
                        `,
                        css`
                            left: 10.4%;
                            top: 42.7%;
                            border-radius: 0 0 78% 0;
                            border-right: 2px solid #f28a11;
                            border-bottom: 2px solid #f28a11;
                            position: absolute;
                            width: 82px;
                            height: 116px;
                            border-left: 2px solid transparent;
                            border-top: 2px solid transparent;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    waveEight: {
        css: css`
            bottom: -2%;
            left: 55%;
            border-radius: 0 70% 0 0;
            border-right: 2px solid #fb1d00;
            border-top: 2px solid #fb1d00;
            border-left: 2px solid transparent;
            border-bottom: 2px solid transparent;
            & > div:first-child {
                border-left: 2px solid #f2a700;
                border-top: 2px solid #f2a700;
                border-right: 2px solid #fb1d00;
                border-bottom: 2px solid #fb1d00;
            }
            @media only screen and (max-width: 1199px) and (min-width: 992px) and (orientation: portrait) {
                width: 65px !important;
            }
            @media only screen and (min-device-width: 481px) and (max-device-width: 1024px) and (orientation: landscape) {
                width: 65px !important;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            width: 43px !important;
                        `,
                        css`
                            top: 49.9%;
                            left: 49.5%;
                            border-radius: 0 0 0 70%;
                            border-left: 2px solid #fb1d00;
                            border-bottom: 2px solid #fb1d00;
                            position: absolute;
                            width: 82px;
                            height: 116px;
                            border-right: 2px solid transparent;
                            border-top: 2px solid transparent;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    waveNine: {
        css: css`
            left: 62%;
            top: -2%;
            border-radius: 0 0 70% 0;
            border-right: 2px solid #fb0000;
            border-bottom: 2px solid #fb0000;
            border-left: 2px solid transparent;
            border-top: 2px solid transparent;
            & > div:first-child {
                border-left: 2px solid #bb01dd;
                border-top: 2px solid #bb01dd;
                border-right: 2px solid #fb0000;
                border-bottom: 2px solid #fb0000;
            }
            @media only screen and (max-width: 1199px) and (min-width: 992px) and (orientation: portrait) {
                width: 65px !important;
            }
            @media only screen and (min-device-width: 481px) and (max-device-width: 1024px) and (orientation: landscape) {
                width: 65px !important;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            left: 11.8% !important;
                            width: 43px !important;
                        `,
                        css`
                            left: 10.4%;
                            top: 57%;
                            border-radius: 0 0 70% 0;
                            border-right: 2px solid #fb0000;
                            border-bottom: 2px solid #fb0000;
                            position: absolute;
                            width: 82px;
                            height: 116px;
                            border-left: 2px solid transparent;
                            border-top: 2px solid transparent;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    waveTen: {
        css: css`
            bottom: -2%;
            left: 70%;
            border-radius: 0 70% 0 0;
            border-right: 2px solid #b3009e;
            border-top: 2px solid #b3009e;
            border-left: 2px solid transparent;
            border-bottom: 2px solid transparent;
            & > div:first-child {
                border-left: 2px solid #b3009e;
                border-top: 2px solid #b3009e;
                border-right: 2px solid #ae00fb;
                border-bottom: 2px solid #ae00fb;
            }
            @media only screen and (max-width: 1199px) and (min-width: 992px) and (orientation: portrait) {
                width: 65px !important;
            }
            @media only screen and (min-device-width: 481px) and (max-device-width: 1024px) and (orientation: landscape) {
                width: 65px !important;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            width: 43px !important;
                        `,
                        css`
                            top: 64.1%;
                            left: 49.5%;
                            border-radius: 0 0 0 70%;
                            border-left: 2px solid #b3009e;
                            border-bottom: 2px solid #b3009e;
                            position: absolute;
                            width: 82px;
                            height: 116px;
                            border-right: 2px solid transparent;
                            border-top: 2px solid transparent;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    waveEleven: {
        css: css`
            left: 78%;
            top: -2%;
            border-radius: 0 0 70% 0;
            border-right: 2px solid #581df8;
            border-bottom: 2px solid #581df8;
            border-left: 2px solid transparent;
            border-top: 2px solid transparent;
            & > div:first-child {
                border-left: 2px solid #a81df5;
                border-top: 2px solid #a81df5;
                border-right: 2px solid #581df8;
                border-bottom: 2px solid #581df8;
            }
            @media only screen and (max-width: 1199px) and (min-width: 992px) and (orientation: portrait) {
                width: 65px !important;
            }
            @media only screen and (min-device-width: 481px) and (max-device-width: 1024px) and (orientation: landscape) {
                width: 65px !important;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            left: 11.8% !important;
                            width: 43px !important;
                        `,
                        css`
                            left: 10.4%;
                            top: 71.6%;
                            border-radius: 0 0 70% 0;
                            border-right: 2px solid #581df8;
                            border-bottom: 2px solid #581df8;
                            position: absolute;
                            width: 82px;
                            height: 116px;
                            border-left: 2px solid transparent;
                            border-top: 2px solid transparent;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    waveTwelve: {
        css: css`
            // background: orange;
            left: 90%;
            top: -3%;
            border-radius: 0 0 70% 0;
            border-right: 2px solid #00a5fb;
            border-bottom: 2px solid #00a5fb;
            border-left: 2px solid transparent;
            border-top: 2px solid transparent;
            & > div:first-child {
                border-left: 2px solid #a81df5;
                border-top: 2px solid #a81df5;
                border-right: 2px solid #581df8;
                border-bottom: 2px solid #581df8;
            }
            @media only screen and (max-width: 1199px) and (min-width: 992px) and (orientation: portrait) {
                width: 80px !important;
            }
            @media only screen and (min-device-width: 481px) and (max-device-width: 1024px) and (orientation: landscape) {
                width: 80px !important;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            width: 43px !important;
                            left: 48.7% !important;
                        `,
                        css`
                            top: 81%;
                            left: 49.7%;
                            border-radius: 0 0 0 70%;
                            border-left: 2px solid #1d7af8;
                            border-bottom: 2px solid #1d7af8;
                            position: absolute;
                            width: 82px;
                            height: 82px;
                            border-right: 2px solid transparent;
                            border-top: 2px solid transparent;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    waveThirteen: { 
        css: css`
            // background: pink;
            bottom: -2%;
            left: 85%;
            border-radius: 0 70% 0 0;
            border-right: 2px solid #581df8;
            border-top: 2px solid #581df8;
            border-left: 2px solid transparent;
            border-bottom: 2px solid transparent;
            & > div:first-child {
                border-left: 2px solid #00a5fb;
                border-top: 2px solid #00a5fb;
                border-right: 2px solid #00b3b1;
                border-bottom: 2px solid #00b3b1;
            }
            @media only screen and (max-width: 1199px) and (min-width: 992px) and (orientation: portrait) {
                width: 65px !important;
            }
            @media only screen and (min-device-width: 481px) and (max-device-width: 1024px) and (orientation: landscape) {
                width: 65px !important;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            left: 22.8% !important;
                            width: 33px !important;
                        `,
                        css`
                            left: 22.4%;
                            top: 81.6%;
                            border-radius: 0 0 70% 0;
                            border-right: 2px solid #581df8;
                            border-bottom: 2px solid #581df8;
                            position: absolute;
                            width: 58px;
                            height: 173px;
                            border-left: 2px solid transparent;
                            border-top: 2px solid transparent;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    topRow: {
        css: css`
            width:100%;
            // width: 92%;
            margin-right: 8%;
            align-items: flex-end;
            & > div {
                width: 100% !important;
                margin: 0;
                align-items: flex-end;
            }
            .historycontent {
                display: flex;
                flex-direction: column;
            }
            .paragraph {
                font-size: ${TYPOGRAPHY.size.tiny};
            }
            hr .1973hr {
                background: red !important;
            }
            hr {
                width: 100%;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            margin-top: 5.7rem;
                            & > div {
                                margin-bottom: 10px;
                            }
                            .historycontent {
                                text-align: right;
                                height: 14.5rem;
                            }
                            .paragraph {
                                order: 2;
                            }
                            hr {
                                order: 1;
                            }
                        `,
                    ],
                    "max",
                )}
        `,
    },
    bottomRow: {
        css: css`
            width: 92%;
            margin-left: 8%;
            & > div {
                width: 100% !important;
                margin: 0;
                & > div p:first-child {
                    padding-top: 9px;
                }
                & > div:last-child {
                    p:first-child {
                        // font-size: 22px;
                        // padding-top: 0;
                        font-size: 16px;
                        padding-top: 9px;
                    }
                }
            }
            .paragraph {
                font-size: ${TYPOGRAPHY.size.tiny};
            }
            .historycontent {
                display: flex;
                flex-direction: column;
                p: first-child {
                    margin-bottom: 0;
                }
            }
            hr {
                width: 100%;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            margin-top: 12.9rem;
                            & > div {
                                margin-bottom: 10px;
                            }
                            .historycontent {
                                height: 14.5rem;
                            }
                            & > div p:first-child {
                                padding-top: 0 !important;
                            }
                        `,
                    ],
                    "max",
                )}
        `,
    },
    extraInnerContainer: {
        width: [12, 12, 1, 1, 1],
        css: css`
            color: ${COLORS.white};
            padding: 0;
            position: absolute;
            right: 0;
            // background: pink;
            & :not(:last-child) {
                padding-right: 30px;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            padding-right: 0px !important;
                            position: absolute;
                            right: auto;
                            bottom: -5.5rem;
                            width: 30%;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    innerContainer: {
        width: [12, 12, 2, 2, 2],
        css: css`
            // width: 1.71%;
            color: ${COLORS.white};
            padding: 0;
            & :not(:last-child) {
                padding-right: 30px;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            padding-right: 0px !important;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    mincircleOne: {
        css: css`
            width: 11px;
            height: 11px;
            border-radius: 50%;
            position: absolute;
            right: -7%;
            top: -17%;
        `,
    },
    smMinCircleOne: {
        css: css`
            width: 11px;
            height: 11px;
            border-radius: 50%;
            position: absolute;
            bottom: -6px;
            left: -10px;
        `,
    },
    mincircleTwo: {
        css: css`
            width: 11px;
            height: 11px;
            border-radius: 50%;
            position: absolute;
            right: -7%;
            top: 99%;
        `,
    },
    smMinCircleTwo: {
        css: css`
            width: 11px;
            height: 11px;
            border-radius: 50%;
            position: absolute;
            right: -11px;
            bottom: -5%;
        `,
    },

    boldCircleOne: {
        css: css`
            left: 9%;
            @media only screen and (max-width: 1199px) and (min-width: 992px) and (orientation: portrait) {
                background: transparent !important;
            }
            @media only screen and (min-device-width: 481px) and (max-device-width: 1024px) and (orientation: landscape) {
                background: transparent !important;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            left: 44%;
                            top: 6.5%;
                            position: absolute;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    boldCircleTwo: {
        css: css`
            left: 16.9%;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            left: 44%;
                            top: 13%;
                            position: absolute;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    boldCircleThree: {
        css: css`
            left: 25%;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            left: 44%;
                            top: 22%;
                            position: absolute;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    boldCircleFour: {
        css: css`
            left: 34%;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            left: 44%;
                            top: 28%;
                            position: absolute;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    boldCircleFive: {
        css: css`
            left: 42%;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            left: 44%;
                            top: 37%;
                            position: absolute;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    boldCircleSix: {
        css: css`
            left: 50.3%;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            left: 44%;
                            top: 47%;
                            position: absolute;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    boldCircleSeven: {
        css: css`
            left: 58.7%;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            left: 44%;
                            top: 57%;
                            position: absolute;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    boldCircleEight: {
        css: css`
            left: 66.9%;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            left: 44%;
                            top: 67%;
                            position: absolute;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    boldCircleNine: {
        css: css`
            left: 75.5%;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            left: 44%;
                            top: 77%;
                            position: absolute;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    boldCircleTen: {
        css: css`
            left: 83.9%;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            left: 44%;
                            top: 87%;
                            position: absolute;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    boldCircleEleven: {
        css: css`
            left: 92.3%;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            left: 44%;
                            top: 87%;
                            position: absolute;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    logo: {
        css: css`
            top: -28px !important;
            // left: 94%;
            left: 100%;
            width: 55px !important;
            height: 55px !important;
            background: #122e4d !important;
            @media only screen and (max-width: 1199px) and (min-width: 992px) and (orientation: portrait) {
                top: -21px !important;
                left: 94%;
                width: 45px !important;
                height: 45px !important;
                background: #122e4d !important;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            left: 30% !important;
                        `,
                        css`
                            top: 88% !important;
                            left: 41%;
                            width: 45px !important;
                            height: 45px !important;
                            background: #122e4d !important;
                            position: absolute;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    gridTop: {
        width: [4, 4, 12, 12, 12],
        css: css`
            padding: 0;
        `,
    },
    gridMiddle: {
        width: [4, 4, 12, 12, 12],
        css: css`
            padding: 0;
            width: 100% !important;
        `,
    },
    gridBottom: {
        width: [4, 4, 12, 12, 12],
        css: css`
            padding: 0;
        `,
    },
    gridmain: {
        css: css`
            & > div {
                width: 100% !important;
                margin: 0;
            }
        `,
    },
    gridMiddleWrap: {
        css: css`
            width: 100%;
        `,
    },
    gridSm: {
        css: css`
            width: 100%;
            height: 100%;
        `,
    },
    smboldCircleOne: {
        css: css`
            background: white;
            height: 15px;
            width: 15px;
            border-radius: 50%;
            position: absolute;
            right: -9px;
            top: -4px;
        `,
    },
    smboldCircleTwo: {
        css: css`
            background: white;
            height: 15px;
            width: 15px;
            border-radius: 50%;
            position: absolute;
            left: -8px;
            top: -4px;
        `,
    },
    extraSmboldCircle: {
        css: css`
            background: white;
            height: 15px;
            width: 15px;
            border-radius: 50%;
            position: absolute;
            left: 23px;
            top: -4px;
            z-index: 1;    
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            left: 23px !important;
                        `,
                        css`
                            left: 48px;
                        `,
                    ],
                    "max",
                )}
        `,
    },
};

const styles = milestoneStyles;

type Props = OwnProps;

const Milestone: FC<Props> = ({ id }) => {
    return (
        <StyledWrapper {...styles.milestone}>
            <StyledWrapper {...styles.title}>
                <Zone contentId={id} zoneName="title" />
            </StyledWrapper>
            <GridContainer {...styles.gridmain}>
                <GridItem {...styles.gridTop}>
                    <GridContainer {...styles.topRow}>
                        <GridItem {...styles.innerContainer}>
                            <Zone contentId={id} zoneName="milestoneOne" />
                        </GridItem>
                        <GridItem {...styles.innerContainer}>
                            <Zone contentId={id} zoneName="milestoneTwo" />
                        </GridItem>
                        <GridItem {...styles.innerContainer}>
                            <Zone contentId={id} zoneName="milestoneThree" />
                        </GridItem>
                        <GridItem {...styles.innerContainer}>
                            <Zone contentId={id} zoneName="milestoneFour" />
                        </GridItem>
                        <GridItem {...styles.innerContainer}>
                            <Zone contentId={id} zoneName="milestoneFive" />
                        </GridItem>
                        <GridItem {...styles.innerContainer}>
                            <Zone contentId={id} zoneName="milestoneSix" />
                        </GridItem>
                        <GridItem {...styles.extraInnerContainer}>
                            <Zone contentId={id} zoneName="milestoneTwelve" />
                        </GridItem>
                    </GridContainer>
                </GridItem>

                <GridItem {...styles.gridMiddle}>
                    <Hidden below="md" {...styles.gridMiddleWrap}>
                        <StyledWrapper {...styles.content}>
                            <StyledWrapper {...styles.line}>
                                <StyledWrapper {...styles.founded}>Founded</StyledWrapper>
                                <StyledWrapper {...styles.boldCircleOne}></StyledWrapper>
                                <StyledWrapper {...styles.boldCircleTwo}></StyledWrapper>
                                <StyledWrapper {...styles.boldCircleThree}></StyledWrapper>
                                <StyledWrapper {...styles.boldCircleFour}></StyledWrapper>
                                <StyledWrapper {...styles.boldCircleFive}></StyledWrapper>
                                <StyledWrapper {...styles.boldCircleSix}></StyledWrapper>
                                <StyledWrapper {...styles.boldCircleSeven}></StyledWrapper>
                                <StyledWrapper {...styles.boldCircleEight}></StyledWrapper>
                                <StyledWrapper {...styles.boldCircleNine}></StyledWrapper>
                                <StyledWrapper {...styles.boldCircleTen}></StyledWrapper>
                                <StyledWrapper {...styles.boldCircleEleven}></StyledWrapper>
                                <StyledWrapper {...styles.logo}>
                                    <Zone contentId={id} zoneName="logo"></Zone>
                                </StyledWrapper>
                            </StyledWrapper>
                            <StyledWrapper {...styles.waveOne}>
                                <StyledWrapper {...styles.mincircleOne}></StyledWrapper>
                            </StyledWrapper>
                            <StyledWrapper {...styles.waveTwo}>
                                <StyledWrapper {...styles.mincircleTwo}></StyledWrapper>
                            </StyledWrapper>
                            <StyledWrapper {...styles.waveThree}>
                                <StyledWrapper {...styles.mincircleOne}></StyledWrapper>
                            </StyledWrapper>
                            <StyledWrapper {...styles.waveFour}>
                                <StyledWrapper {...styles.mincircleTwo}></StyledWrapper>
                            </StyledWrapper>
                            <StyledWrapper {...styles.waveFive}>
                                <StyledWrapper {...styles.mincircleOne}></StyledWrapper>
                            </StyledWrapper>
                            <StyledWrapper {...styles.waveSix}>
                                <StyledWrapper {...styles.mincircleTwo}></StyledWrapper>
                            </StyledWrapper>
                            <StyledWrapper {...styles.waveSeven}>
                                <StyledWrapper {...styles.mincircleOne}></StyledWrapper>
                            </StyledWrapper>
                            <StyledWrapper {...styles.waveEight}>
                                <StyledWrapper {...styles.mincircleTwo}></StyledWrapper>
                            </StyledWrapper>
                            <StyledWrapper {...styles.waveNine}>
                                <StyledWrapper {...styles.mincircleOne}></StyledWrapper>
                            </StyledWrapper>
                            <StyledWrapper {...styles.waveTen}>
                                <StyledWrapper {...styles.mincircleTwo}></StyledWrapper>
                            </StyledWrapper>
                            <StyledWrapper {...styles.waveEleven}>
                                <StyledWrapper {...styles.mincircleOne}></StyledWrapper>
                            </StyledWrapper>
                            <StyledWrapper {...styles.waveTwelve}>
                                <StyledWrapper {...styles.mincircleOne}></StyledWrapper>
                            </StyledWrapper>
                            <StyledWrapper {...styles.waveThirteen}>
                                <StyledWrapper {...styles.mincircleTwo}></StyledWrapper>
                            </StyledWrapper>
                        </StyledWrapper>
                    </Hidden>
                    <Hidden above="md" {...styles.gridSm}>
                        <StyledWrapper {...styles.smContent}>
                            <StyledWrapper {...styles.line}></StyledWrapper>
                            <StyledWrapper {...styles.founded}>Founded</StyledWrapper>
                            <StyledWrapper {...styles.waveOne}>
                                <StyledWrapper {...styles.smMinCircleOne}></StyledWrapper>
                            </StyledWrapper>
                            <StyledWrapper {...styles.waveTwo}>
                                <StyledWrapper {...styles.smMinCircleTwo}></StyledWrapper>
                                <StyledWrapper {...styles.smboldCircleTwo}></StyledWrapper>
                            </StyledWrapper>
                            <StyledWrapper {...styles.waveThree}>
                                <StyledWrapper {...styles.smMinCircleOne}></StyledWrapper>
                                <StyledWrapper {...styles.smboldCircleOne}></StyledWrapper>
                            </StyledWrapper>
                            <StyledWrapper {...styles.waveFour}>
                                <StyledWrapper {...styles.smMinCircleTwo}></StyledWrapper>
                                <StyledWrapper {...styles.smboldCircleTwo}></StyledWrapper>
                            </StyledWrapper>
                            <StyledWrapper {...styles.waveFive}>
                                <StyledWrapper {...styles.smMinCircleOne}></StyledWrapper>
                                <StyledWrapper {...styles.smboldCircleOne}></StyledWrapper>
                            </StyledWrapper>
                            <StyledWrapper {...styles.waveSix}>
                                <StyledWrapper {...styles.smMinCircleTwo}></StyledWrapper>
                                <StyledWrapper {...styles.smboldCircleTwo}></StyledWrapper>
                            </StyledWrapper>
                            <StyledWrapper {...styles.waveSeven}>
                                <StyledWrapper {...styles.smMinCircleOne}></StyledWrapper>
                                <StyledWrapper {...styles.smboldCircleOne}></StyledWrapper>
                            </StyledWrapper>
                            <StyledWrapper {...styles.waveEight}>
                                <StyledWrapper {...styles.smMinCircleTwo}></StyledWrapper>
                                <StyledWrapper {...styles.smboldCircleTwo}></StyledWrapper>
                            </StyledWrapper>
                            <StyledWrapper {...styles.waveNine}>
                                <StyledWrapper {...styles.smMinCircleOne}></StyledWrapper>
                                <StyledWrapper {...styles.smboldCircleOne}></StyledWrapper>
                            </StyledWrapper>
                            <StyledWrapper {...styles.waveTen}>
                                <StyledWrapper {...styles.smMinCircleTwo}></StyledWrapper>
                                <StyledWrapper {...styles.smboldCircleTwo}></StyledWrapper>
                            </StyledWrapper>
                            <StyledWrapper {...styles.waveEleven}>
                                <StyledWrapper {...styles.smMinCircleOne}></StyledWrapper>
                                <StyledWrapper {...styles.smboldCircleOne}></StyledWrapper>
                            </StyledWrapper>
                            <StyledWrapper {...styles.waveThirteen}>
                                <StyledWrapper {...styles.smMinCircleOne}></StyledWrapper>
                                <StyledWrapper {...styles.extraSmboldCircle}></StyledWrapper>
                            </StyledWrapper>
                            <StyledWrapper {...styles.waveTwelve}>
                                <StyledWrapper {...styles.smMinCircleTwo}></StyledWrapper>
                            </StyledWrapper>
                            <StyledWrapper {...styles.logo}>
                                <Zone contentId={id} zoneName="logo"></Zone>
                            </StyledWrapper>
                        </StyledWrapper>
                    </Hidden>
                </GridItem>
                <GridItem {...styles.gridBottom}>
                    <GridContainer {...styles.bottomRow}>
                        <GridItem {...styles.innerContainer}>
                            <Zone contentId={id} zoneName="milestoneSeven" />
                        </GridItem>
                        <GridItem {...styles.innerContainer}>
                            <Zone contentId={id} zoneName="milestoneEight" />
                        </GridItem>
                        <GridItem {...styles.innerContainer}>
                            <Zone contentId={id} zoneName="milestoneNine" />
                        </GridItem>
                        <GridItem {...styles.innerContainer}>
                            <Zone contentId={id} zoneName="milestoneTen" />
                        </GridItem>
                        <GridItem {...styles.innerContainer}>
                            <Zone contentId={id} zoneName="milestoneEleven" />
                        </GridItem>
                        <GridItem {...styles.innerContainer}>
                            <Zone contentId={id} zoneName="milestoneThirteen" />
                        </GridItem>
                        {/* <GridItem {...styles.innerContainer}>
                            <Zone contentId={id} zoneName="milestoneTwelve" />
                        </GridItem> */}
                    </GridContainer>
                </GridItem>
            </GridContainer>
        </StyledWrapper>
    );
};

const milestone: WidgetModule = {
    component: Milestone,
    definition: {
        group: "Common",
        fieldDefinitions: [],
    },
};

export default milestone;
